import { createContext, useContext, useState, useEffect } from "react"
import {
  onAuthStateChanged,
  getAuth,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  EmailAuthProvider,
  signInWithEmailAndPassword,
} from "firebase/auth"
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore"
import { db, googleProvider } from "../firebase"
import LoadingAnimation from "../components/LoadingAnimation"

const AuthContext = createContext()

export const useAuth = () => useContext(AuthContext)

const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null)
  const [role, setRole] = useState(null)
  const [libraryId, setLibraryId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pendingCredential, setPendingCredential] = useState(null)
  const [pendingEmail, setPendingEmail] = useState(null)

  useEffect(() => {
    const auth = getAuth()

    // Handle auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true)
      if (user) {
        setCurrentUser(user)
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid))
          if (userDoc.exists()) {
            const userData = userDoc.data()
            setRole(userData.role || null)
            setLibraryId(userData.libraryId || null)
          } else {
            console.warn("No user data found.")
          }
        } catch (error) {
          console.error("Error fetching user data:", error)
        }
      } else {
        setCurrentUser(null)
        setRole(null)
        setLibraryId(null)
      }
      setLoading(false)
    })

    // Cleanup subscription on unmount
    return () => unsubscribe()
  }, [])

  // Function to set the library ID for the current user
  const setCurrentUserLibraryId = async (adminId) => {
    try {
      const adminDoc = await getDoc(doc(db, "users", adminId))
      if (adminDoc.exists()) {
        const adminData = adminDoc.data()
        setLibraryId(adminData.libraryId || null)
      } else {
        console.warn("No admin data found.")
      }
    } catch (error) {
      console.error("Error fetching admin data:", error)
    }
  }

  // Function to handle Google Sign-In with improved account linking and role preservation
  const googleSignIn = async () => {
    const auth = getAuth()
    try {
      // Sign in with popup window
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user

      // Check if user exists in Firestore
      const userDocRef = doc(db, "users", user.uid)
      const userDoc = await getDoc(userDocRef)

      // Extract name from email if displayName is not available
      const nameFromEmail = user.email ? user.email.split("@")[0].replace(/[^a-zA-Z0-9]/g, " ") : ""
      // Capitalize first letter of each word
      const formattedName = nameFromEmail
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")

      if (!userDoc.exists()) {
        // Create a new user document for Google sign-ins
        await setDoc(userDocRef, {
          name: user.displayName || formattedName,
          email: user.email || "",
          photoURL: user.photoURL || "",
          createdAt: serverTimestamp(),
          role: "user", // Default role for new users
          authProvider: "google",
        })

        setRole("user")
        setLibraryId(null)
      } else {
        // User exists, get their role and libraryId
        const userData = userDoc.data()
        setRole(userData.role || null)
        setLibraryId(userData.libraryId || null)
      }

      // Store user info in localStorage
      localStorage.setItem("userId", user.uid)
      localStorage.setItem("role", userDoc.exists() ? userDoc.data().role || "user" : "user")
      localStorage.setItem("libraryId", userDoc.exists() && userDoc.data().libraryId ? userDoc.data().libraryId : "")

      return {
        user,
        role: userDoc.exists() ? userDoc.data().role || "user" : "user",
        libraryId: userDoc.exists() && userDoc.data().libraryId ? userDoc.data().libraryId : null,
      }
    } catch (error) {
      console.error("Google sign-in error:", error)

      // Handle account exists with different credential
      if (error.code === "auth/account-exists-with-different-credential") {
        const email = error.customData.email
        const auth = getAuth()

        // Save the Google credential for later use
        const pendingCred = GoogleAuthProvider.credentialFromError(error)
        setPendingCredential(pendingCred)
        setPendingEmail(email)

        // Fetch sign-in methods for the email
        const methods = await fetchSignInMethodsForEmail(auth, email)

        // Return information about the conflict
        return {
          error: "auth/account-exists-with-different-credential",
          email,
          methods,
          message: `An account already exists with the same email address but different sign-in method. Please sign in using ${methods[0]}.`,
        }
      }

      throw error
    }
  }

  // Function to link Google account with existing email/password account
  const linkWithGoogle = async (password) => {
    if (!pendingCredential || !pendingEmail) {
      throw new Error("No pending credential to link")
    }

    try {
      const auth = getAuth()

      // Sign in with email and password
      const emailCredential = EmailAuthProvider.credential(pendingEmail, password)

      // First, sign in with email/password
      const userCredential = await signInWithEmailAndPassword(auth, pendingEmail, password)
      const user = userCredential.user

      // Get the user's data to preserve role
      const userDocRef = doc(db, "users", user.uid)
      const userDoc = await getDoc(userDocRef)
      let role = "user"
      let libraryId = null

      if (userDoc.exists()) {
        const userData = userDoc.data()
        role = userData.role || "user"
        libraryId = userData.libraryId || null
      }

      // Link the accounts
      await linkWithCredential(user, pendingCredential)

      // Update user document to reflect multiple auth providers
      // but preserve the existing role
      await setDoc(
        userDocRef,
        {
          authProviders: ["password", "google"],
          lastUpdated: serverTimestamp(),
        },
        { merge: true },
      )

      // Clear pending credential
      setPendingCredential(null)
      setPendingEmail(null)

      // Update context
      setRole(role)
      setLibraryId(libraryId)

      return {
        success: true,
        user,
        role,
        libraryId,
      }
    } catch (error) {
      console.error("Error linking accounts:", error)
      throw error
    }
  }

  // Function to handle logout
  const logout = async () => {
    const auth = getAuth()
    try {
      await signOut(auth)
      // State will be cleared by the onAuthStateChanged listener
    } catch (error) {
      console.error("Logout failed:", error)
      throw error
    }
  }

  // Show loading animation while fetching user data
  if (loading) {
    return <LoadingAnimation />
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        role,
        setRole,
        libraryId,
        setLibraryId,
        setCurrentUserLibraryId,
        googleSignIn,
        linkWithGoogle,
        pendingEmail,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
